/**
 * WavefolderModule — west-coast-style harmonic generation by
 * reflecting the waveform back when it crosses ±1 instead of clipping
 * (which is what Distortion does). Produces a brassy, FM-like
 * harmonic series rich in even harmonics — the "Buchla" sound.
 *
 * Graph:
 *
 *   input ─┬─ dryGain ─────────────────────────────────── outputMix ─►
 *          └─► preGain ─► waveShaper ─► postLP ─► wetGain ─ outputMix
 *
 * `preGain` (the Drive param) pushes the input past unity so the
 * waveshaper has something to fold. The shaper's curve is built from
 * a sine: `out = sin(x * π * fold)`, which produces a clean triangle
 * approximation at fold=1 and increasingly busy harmonic stacks as
 * fold rises. A gentle post-LP rolls off the highest aliasing risk
 * without dulling the character.
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

/** Waveshaper curve resolution — high enough that a single fold pass
 *  doesn't introduce visible quantization artifacts. */
const CURVE_SAMPLES = 2048;

function buildFoldCurve(fold: number): Float32Array<ArrayBuffer> {
  // Allocate explicit ArrayBuffer to satisfy the WaveShaperNode.curve
  // typing (Float32Array<ArrayBuffer> specifically).
  const out = new Float32Array(new ArrayBuffer(CURVE_SAMPLES * 4));
  // sin(π * x * fold) maps a -1..+1 input to a folded -1..+1 output.
  // fold=1   → near-triangle (one peak / trough each side)
  // fold=4   → multiple folds → strong upper harmonics
  // fold=8   → busy, FM-like
  for (let i = 0; i < CURVE_SAMPLES; i++) {
    const x = (i / (CURVE_SAMPLES - 1)) * 2 - 1; // -1..+1
    out[i] = Math.sin(Math.PI * x * fold);
  }
  return out;
}

export class WavefolderModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'wavefolder' as const;
  readonly displayName = 'FOLD';
  readonly id: string;
  bypass = false;

  readonly drive = new NumericParameter({
    name: 'Drive',
    default: 0.4,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 30,
    // Bias moderate — a fold with no drive is just a quiet pass-through.
    randomSkew: 0.7,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  readonly fold = new NumericParameter({
    name: 'Fold',
    default: 2,
    min: 1,
    max: 8,
    curve: 'linear',
    smoothingMs: 30,
    randomSkew: 0.6,
    formatter: (v) => v.toFixed(2),
  });
  readonly tone = new NumericParameter({
    name: 'Tone',
    default: 8000,
    min: 200,
    max: 20000,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 15,
    randomSkew: 3,
    formatter: (v) =>
      v >= 1000 ? `${(v / 1000).toFixed(2)}k` : `${v.toFixed(0)}`,
  });
  readonly mix = new NumericParameter({
    name: 'Mix',
    default: 0.6,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    randomSkew: 0.7,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });

  readonly parameters: readonly AnyParameter[] = [
    this.drive,
    this.fold,
    this.tone,
    this.mix,
  ];

  private engine: AudioEngine | null = null;
  private inputNode: GainNode | null = null;
  private outputMixNode: GainNode | null = null;
  private preGain: GainNode | null = null;
  private waveshaper: WaveShaperNode | null = null;
  private postLP: BiquadFilterNode | null = null;
  private dryGain: GainNode | null = null;
  private wetGain: GainNode | null = null;
  private started = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(id?: string) {
    this.id = id ?? mintId('fold');
  }

  get input(): AudioNode | null {
    return this.inputNode;
  }
  get output(): AudioNode | null {
    return this.outputMixNode;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;

    this.inputNode = ctx.createGain();
    this.outputMixNode = ctx.createGain();
    this.preGain = ctx.createGain();
    this.waveshaper = ctx.createWaveShaper();
    this.waveshaper.oversample = '2x';
    this.waveshaper.curve = buildFoldCurve(this.fold.value);

    this.postLP = ctx.createBiquadFilter();
    this.postLP.type = 'lowpass';
    this.postLP.frequency.value = this.tone.value;
    this.postLP.Q.value = 0.5;

    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();

    // Wet path: input → preGain → waveshaper → tone → wetGain
    this.inputNode.connect(this.preGain);
    this.preGain.connect(this.waveshaper);
    this.waveshaper.connect(this.postLP);
    this.postLP.connect(this.wetGain);
    this.wetGain.connect(this.outputMixNode);

    // Dry path
    this.inputNode.connect(this.dryGain);
    this.dryGain.connect(this.outputMixNode);

    this.applyPreGain(this.drive.value);
    this.applyMix(this.mix.value, /* instant */ true);
    this.tone.bindAudioParam(this.postLP.frequency, ctx);

    // Drive scales preGain; Fold rebuilds the curve.
    this.unsubs.push(this.drive.subscribe((v) => this.applyPreGain(v)));
    this.unsubs.push(
      this.fold.subscribe((v) => {
        if (this.waveshaper) this.waveshaper.curve = buildFoldCurve(v);
      }),
    );
    this.unsubs.push(this.mix.subscribe((v) => this.applyMix(v)));

    this.started = true;
  }

  /**
   * Mod-routing lookup. Tone is the only AudioParam-friendly
   * destination; Drive scales a gain (not an AudioParam target since
   * we do the maths in JS to keep it monotonic), Fold reshapes the
   * curve on every change, and Mix drives the dry/wet gains via
   * setTargetAtTime. The matrix can still drive Drive/Fold/Mix via
   * the macro path which writes their NumericParameter directly.
   */
  getAudioParam(paramName: string): AudioParam | null {
    if (paramName === 'Tone') return this.postLP?.frequency ?? null;
    return null;
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    this.inputNode?.disconnect();
    this.preGain?.disconnect();
    this.waveshaper?.disconnect();
    this.postLP?.disconnect();
    this.dryGain?.disconnect();
    this.wetGain?.disconnect();
    this.outputMixNode?.disconnect();
    this.inputNode = null;
    this.outputMixNode = null;
    this.waveshaper = null;
    this.preGain = null;
    this.postLP = null;
    this.dryGain = null;
    this.wetGain = null;
    this.started = false;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }

  private applyPreGain(drive: number): void {
    if (!this.engine || !this.preGain) return;
    // Push up to 6× at full drive — enough headroom for several
    // folds without introducing painful aliasing. The post-LP
    // catches anything that does slip through.
    const g = 1 + drive * 5;
    const t = this.engine.context.currentTime;
    this.preGain.gain.cancelScheduledValues(t);
    this.preGain.gain.setTargetAtTime(g, t, 0.02);
  }

  private applyMix(mix: number, instant = false): void {
    if (!this.engine || !this.dryGain || !this.wetGain) return;
    // Equal-power crossfade — same as Distortion so the dry/wet
    // balance feels consistent across coloration effects.
    const dry = Math.cos((mix * Math.PI) / 2);
    const wet = Math.sin((mix * Math.PI) / 2);
    const t = this.engine.context.currentTime;
    if (instant) {
      this.dryGain.gain.setValueAtTime(dry, t);
      this.wetGain.gain.setValueAtTime(wet, t);
    } else {
      this.dryGain.gain.cancelScheduledValues(t);
      this.wetGain.gain.cancelScheduledValues(t);
      this.dryGain.gain.setTargetAtTime(dry, t, 0.02);
      this.wetGain.gain.setTargetAtTime(wet, t, 0.02);
    }
  }
}
